import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import {
  IDeliveryRoute,
  IDrop
} from "src/core/drops/api/drops.api";
import { IPrice } from "src/core/shared/api/price-change.api";
import useAxios from "src/core/shared/hooks/useAxios";
import { ITag } from "src/modules/product-tags/api/product-tags.api";
import { IProductSubstitution } from "src/modules/substitutions/api/substitutions.api";
import { IPromotionalTag } from "src/modules/promotions/api/promotional-tags.api";
import { OrderType } from "src/core/orders/api/orders.api";
import { ICustomer } from "src/core/customers/api/customers.api";
import { CreateOpenDayFilterAttributes } from "src/modules/shutdowns/api/open-day.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import { IProcessJob } from "src/core/process-job/api/processJobs.api";
import { Icons } from "src/library/components/Icon/types";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";
import { ISupplier } from "src/core/suppliers/api/suppliers.api";

export type CreateProductParams = Pick<
  IProduct,
  | "description"
  | "categories"
  | "image"
  | "published"
  | "leadTimeGroupId"
  | "infoKey"
  | "partNumber"
  | "madeToOrder"
  | "ingredients"
  | "nutritionImage"
  | "barcode"
  | "minimumQuantity"
  | "supplierId"
> & { vatPercentage: number; price: number };

export type GetProductParams = { productId: number; filters?: IGetProductFilters | undefined };
export type GetProductResponse = IProduct & {substitution?: Omit<IProductSubstitution, "previousProduct">};
export type UpdatePriceParam = Omit<IPrice, "id"> & { id?: number };
export type ListProductsParams = { pagination: PaginatedParams<IProduct>; filters: ListProductsFilters };
export type ListProductsFilters = FilterParams<Omit<IProduct, "prices">> & {
  priceCodeId?: number;
  specialPriceCodeId?: number;
  productApprovalListId?: number;
  effectiveDate?: Date;
  branchId?: number;
  includeDelisted?: boolean;
  includeUnpublished?: boolean;
  includeCreditOnly?: boolean;
} ;

export type ListProductsResponse = PaginatedData<IProduct> & { substitutions: IProductSubstitution[], featuredProducts?: IProduct[] };

export type ProductExportParams = { filters: FilterParams<Pick<IProduct, "published">> };
export type CreateProductCategoryParams = Pick<IProductCategory, "name" | "parentId">;
export type UpdateProductCategoryParams = IProductCategory;
export type ListProductCategoriesParams = {
  filters: FilterParams<{
    orderDate?: Date,
    name?: string;
  }>;
  branchId: number | null
};
export type ListProductCategoriesResponse = IProductCategory[];

export interface ProductReplacement {
  fromProduct?: IProduct;
  toProduct?: IProduct;
}

export interface ProductReplacementId {
  fromProductId: number;
  toProductId: number;
}

export type BulkUpdateCategorySortOrderParams = Array<Pick<IProductCategory, "id" | "sortOrder">>;
export type CreateProductReplacementParams = {
  replacements: ProductReplacementId[]
  type: CustomerBranchFilterDefault;
  orderTypes: OrderType[];
  filters: GlobalProductReplacementFilter[];
  effectOrders?: {
    startDate: Date;
    endDate?: Date;
  };
  effectLists: boolean;
};

export interface IGetProductFilters {
  branchId?: number;
  effectiveDate?: string | Date;
}

export type GetHistoricallyOrderedProductsByBranchParams = {
  branchId: number;
  range?: {
    startDate?: Date;
    endDate?: Date;
  };
};

type ProductsEndpointConfig = {
  LIST_PRODUCTS: EndpointConfig<
    ListProductsParams,
    ListProductsResponse
  >;
  GET_PRODUCT: EndpointConfig<
    GetProductParams,
    GetProductResponse
  >;
  CREATE_PRODUCT: EndpointConfig<
    CreateProductParams,
    IProduct
  >;
  UPDATE_PRODUCT: EndpointConfig<
    UpdateProductParams,
    IProduct
  >;
  LIST_PRODUCT_CATEGORIES: EndpointConfig<
    ListProductCategoriesParams,
    ListProductCategoriesResponse
  >;
  GET_PRODUCT_CATEGORY: EndpointConfig<
    number,
    IProductCategory
  >;
  CREATE_PRODUCT_CATEGORY: EndpointConfig<
    CreateProductCategoryParams,
    IProductCategory
  >;
  UPDATE_PRODUCT_CATEGORY: EndpointConfig<
    Partial<IProductCategory>,
    IProductCategory
  >;
  BULK_UPDATE_CATEGORY_SORT_ORDER: EndpointConfig<
    BulkUpdateCategorySortOrderParams,
    IProductCategory[]
  >;
  DELETE_PRODUCT_CATEGORY: EndpointConfig<
    number,
    IProductCategory
  >;
  EXPORT_PRODUCTS: EndpointConfig<
    ProductExportParams,
    { message: string }
  >;
  GET_ALL_LEAD_TIMES: EndpointConfig<
    null,
    IProductLeadTimeGroup[]
  >;
  CREATE_PRODUCT_REPLACEMENT: EndpointConfig<
    CreateProductReplacementParams,
    IProcessJob<"global-product-replacement">
  >;
  GET_DISTINCT_PACK_SIZE_CODES: EndpointConfig<
    null,
    string[]
  >;
  GET_HISTORICALLY_ORDERED_PRODUCTS_BY_BRANCH: EndpointConfig<
    GetHistoricallyOrderedProductsByBranchParams,
    IProduct[]
  >;
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/core/products`;

const requests: RequestConfig<ProductsEndpointConfig> = {
  LIST_PRODUCTS: params => ({
    url: `${BASE_URL}`,
    method: "GET",
    params,
    cache: { exclude: { query: true } }
  }),
  GET_PRODUCT: params => ({
    url: `${BASE_URL}/${params.productId}`,
    method: "GET",
    params: { filters: params.filters }
  }),
  CREATE_PRODUCT: data => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/products`,
    method: "POST",
    data
  }),
  UPDATE_PRODUCT: data => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/products/${data.id}`,
    method: "PATCH",
    data
  }),
  LIST_PRODUCT_CATEGORIES: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/categories`,
    method: "GET",
    params
  }),
  GET_PRODUCT_CATEGORY: categoryId => ({
    url: `${process.env.REACT_APP_API_URL}/core/categories/${categoryId}`,
    method: "GET"
  }),
  CREATE_PRODUCT_CATEGORY: data => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/categories`,
    method: "POST",
    data
  }),
  UPDATE_PRODUCT_CATEGORY: data => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/categories/${data.id}`,
    method: "PATCH",
    data
  }),
  BULK_UPDATE_CATEGORY_SORT_ORDER: data => ({
    url: `${process.env.REACT_APP_API_URL}/core/categories/update/sort-order`,
    method: "PATCH",
    data
  }),
  DELETE_PRODUCT_CATEGORY: id => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/categories/${id}`,
    method: "DELETE"
  }),
  EXPORT_PRODUCTS: params => ({
    url: `${BASE_URL}/export/all`,
    method: "GET",
    params
  }),
  GET_ALL_LEAD_TIMES: () => ({
    url: `${BASE_URL}/lead-times/all`,
    method: "GET"
  }),
  CREATE_PRODUCT_REPLACEMENT: data => ({
    url: `${BASE_URL}/global-replacement/create`,
    method: "POST",
    data
  }),
  GET_DISTINCT_PACK_SIZE_CODES: () => ({
    url: `${BASE_URL}/pack-sizes/distinct-codes`,
    method: "GET",
    cache: {
      clearOnError: true,
      maxAge: cacheMinutesToMilliseconds(60)
    }
  }),
  GET_HISTORICALLY_ORDERED_PRODUCTS_BY_BRANCH: params => ({
    url: `${BASE_URL}/branch/${params.branchId}`,
    method: "GET",
    params
  })
};

export interface IProduct {
  id: number;
  image?: string;
  description: string;
  partNumber: string | null;
  currentStatus?: IProductStatus;
  productStatuses?: IProductStatus[];
  leadTimeGroupId: number;
  published: boolean;
  shelfLife?: number | null;
  shelfLifePeriod: ShelfLifePeriod | null;
  packSizeId?: number;
  categories?: IProductCategory[];
  ingredients?: string;
  nutritionImage?: string;
  nutritionValues?: IProductNutritionValue[];
  infoKey?: string;
  prices?: IPrice[];
  packSizes?: IPackSize[];
  madeToOrder: boolean;
  creditOnly: boolean;
  deliveryRoute?: [IDeliveryRoute] | [];
  tags?: ITag[];
  customData?: ProductCustomData;
  barcode?: string;
  minimumQuantity?: number | null;
  /** Currently assumed KG */
  weight?: number | null;
  promotionalTags?: IPromotionalTag[];
  leadTimeGroup?: IProductLeadTimeGroup;
  delisted?: boolean;
  sortOrder?: number | null;
  relatedGroupId?: number | null;
  unitCode?: string | null;
  unitCodePlural?: string | null;
  supplierId?: number | null;
  supplier?: ISupplier;
  disableUnitOrdering?: boolean | null;
  supplierPartNumber?: string | null;
}

export type UpdateProductParams = Pick<IProduct, "id"> & Partial<IProduct>;

export interface IProductLeadTimeGroup {
  id: number;
  name: string;
  customerDescription?: string;
  defaultLeadTimeDays: number;
}

export interface IPackSize {
  id: number;
  name: string;
  code: string;
  quantity: number;
  namePlural?: string | null;
}

export enum ShelfLifePeriod {
  Days = "days",
  Months = "months"
}
export enum ProductStatus {
  Suspended = "suspended",
  Active = "active",
  Delisted = "delisted"
}

export interface IProductStatus {
  id?: number;
  productId: number;
  status: ProductStatus;
  startDate: Date | string;
  endDate?: Date | string | null;
}

export interface IProductCategory {
  id: number;
  name: string;
  parentCategory?: IProductCategory;
  parentId: number | null;
  sortOrder: number | null;
}

export type ISearchRequest = string;

export interface IProductNutritionValue {
  id: number;
  productId: number;
  measurement: ProductNutritionMeasurement;
  category: ProductNutritionCategory;
  unit: ProductNutritionPer;
  value: number;
}

export enum ProductNutritionMeasurement {
  Grams = "g",
  Kilojoules = "KJ",
  Kilocalories = "kcal"
}

export enum ProductNutritionCategory {
  Sugars = "sugars",
  KCal = "kilocalories",
  KJ = "kilojoules",
  Fat = "fat",
  Protein = "protein",
  Salt = "salt",
  Fibre = "fibre",
  Carbs = "carbohydrates",
  Saturates = "saturates"
}

export enum ProductNutritionPer {
  Per100 = "100",
  PerItem = "item"
}

export type ProductCustomData = Array<{name: string; description: string; value: string}>;

export interface GlobalProductReplacementFilter {
  /** Customer to associate with */
  customerId?: number | null;
  customer?: ICustomer;
  /** Branch to associate with */
  branchId?: number | null;
  branch?: IDrop;
  filterType: CustomerBranchFilterType;
}

export interface IProductRelatedGroup {
  id: number;
  name: string;
  terminology: ProductRelatedGroupTerminology;
  /* if parent id exists, this product related group is a child */
  parentId?: number | null;
  categoryId: number;
  /* settings used to help validate products against this group */
  settings?: ProductRelatedGroupSettings;
  category?: IProductCategory;
}

export interface ProductRelatedGroupTerminology {
  singular: string;
  plural: string;
}

export interface ProductRelatedGroupSettings {
  icon?: Icons;
  parentProducts?: ProductValidationSetting;
  childProducts?: ProductValidationSetting;
}

export interface ProductValidationSetting {
  errorLevel: ErrorLevel;
  message?: string;
}

export type ProductWithPackSizes = Omit<IProduct, "packSizes"> & {packSizes: IPackSize[]};

export enum ErrorLevel {
  Warn = "warn",
  Error = "error"
}

export enum CustomerBranchFilterType {
  Include = "include",
  Exclude = "exclude"
}

export enum CustomerBranchFilterDefault {
  AllowAll = "allow_all",
  AllowNone = "allow_none"
}

export type CreateCustomerBranchFilterAttributes = CreateOpenDayFilterAttributes | GlobalProductReplacementFilter;

export const useProductsApi: UseApiRequest<ProductsEndpointConfig> = name => useAxios(requests[ name ], name);
